import { Link, useParams } from "react-router-dom";
import { Phone, ArrowRight, MapPin, Zap, Building2, ShieldCheck, CheckCircle2, ArrowUpRight } from "lucide-react";
import { getLocality, SERVICES, SITE } from "@/config/site";
import { Seo } from "@/components/Seo";
import { Breadcrumbs, breadcrumbSchema } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { InterventionButton, FinalCTA } from "@/components/blocks";
import { trackEvent } from "@/lib/analytics";
import NotFound from "@/pages/NotFound";

export default function LocalityPage() {
  const { slug } = useParams();
  const loc = getLocality(slug);
  if (!loc) return <NotFound />;

  const crumbs = [{ label: "Zones d'intervention", to: "/zones-intervention" }, { label: loc.name }];
  const title = `Destruction nid de frelons ${loc.name} (${loc.cp}) | FRELONS50`;
  const description = `FRELONS50 intervient à ${loc.name} (${loc.cp}) : destruction de nids de frelons et guêpes, dératisation rats et souris. Contact rapide au ${SITE.phoneDisplay}.`;

  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "PestControlService",
    name: "FRELONS50",
    telephone: "+33635922149",
    email: SITE.email,
    url: `${SITE.url}/zone/${loc.slug}`,
    areaServed: { "@type": "City", name: loc.name },
    address: { "@type": "PostalAddress", addressLocality: "Le Teilleul", postalCode: "50640", addressRegion: "Manche", addressCountry: "FR" },
  };

  return (
    <>
      <Seo title={title} description={description} path={`/zone/${loc.slug}`} jsonLd={[businessSchema, breadcrumbSchema(crumbs)]} />

      {/* HERO compact */}
      <section className="relative bg-brand-black text-white overflow-hidden grain-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="[&_*]:text-white/80 mb-5"><Breadcrumbs items={crumbs} /></div>
          <Reveal className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-brand-gold/15 text-brand-gold border border-brand-gold/30 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5">
              <MapPin className="w-3.5 h-3.5" /> Intervention à {loc.name} · {loc.cp}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-[1.08] tracking-tight">
              Destruction de nids de frelons et traitement des nuisibles à {loc.name}
            </h1>
            <p className="mt-5 text-lg text-white/80 leading-relaxed">{loc.blurb}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={SITE.phoneHref} onClick={() => trackEvent("click_phone", { location: `zone_${loc.slug}` })} data-testid="zone-call-btn" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-brand-gold text-brand-black font-bold hover:-translate-y-0.5 transition-transform">
                <Phone className="w-5 h-5" /> Appeler le {SITE.phoneDisplay}
              </a>
              <InterventionButton dark testid="zone-intervention-btn" />
            </div>
            <ul className="mt-8 flex flex-wrap gap-3">
              {[{ icon: Zap, label: "Intervention rapide" }, { icon: MapPin, label: "Entreprise locale" }, { icon: Building2, label: "Particuliers & pros" }].map((r) => (
                <li key={r.label} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-2 rounded-full text-sm font-medium">
                  <r.icon className="w-4 h-4 text-brand-gold" /> {r.label}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* SERVICES à la commune */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <h2 className="font-display text-2xl md:text-3xl font-extrabold text-brand-black mb-3">Nos interventions à {loc.name}</h2>
        <p className="text-brand-text max-w-2xl mb-8">Sélectionnez votre situation. Nous vous indiquons la solution la plus adaptée à ce qui est observé, sans engagement.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.04}>
              <Link to={`/${s.slug}`} data-testid={`zone-service-${s.slug}`} className="group flex items-center justify-between gap-3 bg-brand-gray rounded-3xl p-6 border border-transparent hover:border-brand-gold/40 hover:shadow-[0_8px_30px_rgba(0,0,0,0.05)] transition-all">
                <div>
                  <h3 className="font-display text-lg font-bold text-brand-black">{s.nav}</h3>
                  <p className="text-sm text-brand-text mt-1">{s.short}</p>
                </div>
                <ArrowUpRight className="w-5 h-5 text-brand-text group-hover:text-brand-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Communes proches — maillage interne */}
      <section className="bg-brand-gray py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-display text-xl font-bold text-brand-black mb-5">Communes proches desservies</h2>
          <div className="flex flex-wrap gap-3">
            {loc.nearby.map((sl) => {
              const n = getLocality(sl);
              if (!n) return null;
              return (
                <Link key={sl} to={`/zone/${sl}`} data-testid={`nearby-${sl}`} className="inline-flex items-center gap-2 bg-white border border-gray-200 px-5 py-3 rounded-full text-sm font-semibold text-brand-black hover:border-brand-gold transition-colors">
                  <MapPin className="w-3.5 h-3.5 text-brand-gold" /> {n.name}
                </Link>
              );
            })}
            <Link to="/zones-intervention" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-brand-text hover:text-brand-black">
              Toutes les zones <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-6 text-sm text-brand-text">
            Votre commune n'est pas listée ?{" "}
            <Link to="/contact" className="text-brand-black font-semibold underline">Contactez-nous pour vérifier notre disponibilité.</Link>
          </p>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
